import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';

export const accountantReviewLineItemSchema = z.object({
  line_item_id: z.string(),
  transaction_id: z.string(),
  posted_at: z.string(),
  amount: z.string(),
  category_id: z.string().nullable(),
  tax_line: z.string().nullable(),
  tax_treatment: z.string().nullable(),
  taxonomy_version: z.string(),
  version: z.number().int().positive(),
}).strict();

const summarySchema = z.object({
  business_row_count: z.number().int().nonnegative(),
  gross_receipts: z.string().nullable(),
  recorded_expenses: z.string().nullable(),
  coverage_status: z.string(),
  reviewed_count: z.number().int().nonnegative(),
});

export const accountantReviewSchema = z.object({
  status: z.enum(['ready', 'empty', 'insufficient']),
  summary: summarySchema,
  coverage: z.array(z.record(z.string(), z.unknown())),
  income_bridge: z.record(z.string(), z.unknown()),
  tieouts: z.array(z.record(z.string(), z.unknown())),
  exceptions: z.array(z.record(z.string(), z.unknown())),
  substantiation: z.array(z.record(z.string(), z.unknown())),
  change_log_summary: z.record(z.string(), z.unknown()),
  changes: z.array(z.record(z.string(), z.unknown())).default([]),
  owner_confirmation: z.record(z.string(), z.unknown()),
  year_lock: z.record(z.string(), z.unknown()),
  as_of: z.string(),
}).strict();

export type AccountantReview = z.infer<typeof accountantReviewSchema>;

export function useAccountantReview(grantId: string | null | undefined, taxYear: number | null | undefined) {
  return useQuery({
    queryKey: ['accountant-review', grantId, taxYear],
    enabled: Boolean(grantId && taxYear != null),
    queryFn: async () => {
      const response = await client.get(`/api/accountant/clients/${grantId}/years/${taxYear}/review`);
      return parseEnvelope(accountantReviewSchema, response.data).data;
    },
  });
}
